import { useEffect, useState } from "react";
import { X, Download, Share } from "lucide-react";

const DISMISS_KEY = "mboazick_pwa_dismissed_at";
const DISMISS_DAYS = 7;

function isIos() {
    return /iphone|ipad|ipod/i.test(navigator.userAgent);
}
function isStandalone() {
    return window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
}
function isMobile() {
    return /android|iphone|ipad|ipod|mobile/i.test(navigator.userAgent);
}
function recentlyDismissed() {
    const t = localStorage.getItem(DISMISS_KEY);
    return t && Date.now() - Number(t) < DISMISS_DAYS * 24 * 3600 * 1000;
}

/** Bottom banner inviting mobile visitors to install the MBOAZICK app (PWA).
 *  Android/Chrome: native install prompt. iOS Safari: manual instructions. */
export default function PwaInstallBanner() {
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [show, setShow] = useState(false);
    const [ios, setIos] = useState(false);

    useEffect(() => {
        if (!isMobile() || isStandalone() || recentlyDismissed()) return;
        if (isIos()) {
            setIos(true);
            const t = setTimeout(() => setShow(true), 2500);
            return () => clearTimeout(t);
        }
        const handler = (e) => {
            e.preventDefault();
            setDeferredPrompt(e);
            setShow(true);
        };
        window.addEventListener("beforeinstallprompt", handler);
        return () => window.removeEventListener("beforeinstallprompt", handler);
    }, []);

    const dismiss = () => {
        localStorage.setItem(DISMISS_KEY, String(Date.now()));
        setShow(false);
    };

    const install = async () => {
        if (!deferredPrompt) return;
        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        if (outcome !== "dismissed") setShow(false);
        else dismiss();
        setDeferredPrompt(null);
    };

    if (!show) return null;

    return (
        <div className="fixed bottom-0 inset-x-0 z-[80] p-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]" data-testid="pwa-install-banner">
            <div className="max-w-md mx-auto bg-[#0F0F0F] border border-[#00d97e]/40 rounded-2xl shadow-[0_-8px_40px_-10px_rgba(0,217,126,0.35)] p-4 flex items-center gap-3">
                <img decoding="async" loading="lazy" src="/icons/icon-192.png" alt="MBOAZICK" className="w-12 h-12 rounded-xl shrink-0" />
                <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm text-[#F4F4F4]">Installe l&apos;app MBOAZICK</p>
                    {ios ? (
                        <p className="text-[11px] text-[#A8A8A8] leading-snug">
                            Appuie sur <Share className="w-3 h-3 inline text-[#00c0ff]" /> <strong>Partager</strong> puis
                            « <strong>Sur l&apos;écran d&apos;accueil</strong> »
                        </p>
                    ) : (
                        <p className="text-[11px] text-[#A8A8A8]">Accès rapide depuis ton écran d&apos;accueil, comme une vraie app.</p>
                    )}
                </div>
                {!ios && (
                    <button onClick={install} className="shrink-0 inline-flex items-center gap-1.5 bg-[#00d97e] hover:bg-[#00f099] text-[#050505] font-semibold px-4 py-2 rounded-full text-xs" data-testid="pwa-install-btn">
                        <Download className="w-3.5 h-3.5" /> Installer
                    </button>
                )}
                <button onClick={dismiss} className="shrink-0 p-1.5 text-[#6E6E6E] hover:text-[#F4F4F4]" aria-label="Fermer" data-testid="pwa-dismiss-btn">
                    <X className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
}
